import { useDispatch, useSelector } from "react-redux";

// Actions
import { toggleRouteTypeSelection } from "../../../actions/org/OrgRouteTypesActions";
import { closeTabContentPreviewForm } from "../../actions/campaign-planning/TabContentPreviewFormActions";
import { unSelectZone } from "../../actions/campaign-planning/ZonesAndSubZonesActions";
import {
  toggleMediaType,
  unSelectTgAttribute,
  unSelectTouchPoint,
} from "../../actions/campaign-planning/CampaignPlanningActions";
import { unSelectPoiBrands } from "../../../actions/map-view/poi/PoiSelectionActions";
import { setPoiStatus } from "../../../actions/map-view/poi/PoiActions";
import { unselectDataLayer } from "../../../actions/map-view/DataLayerSelectionActions";

// Constants and Utils
import { ProohFormDataTargets } from "../../constants/GeneralConstants";
import {
  MediaTypeCategoriesColors,
  Status,
} from "../../../constants/GeneralConstants";
import { getCityConfigAllMediaTypes } from "./CampaignPlanningUtils";

// Components
import Chips from "../../../components/Chips/Chips";

// Page Functions
function constructSelectedTgs(selectedTgAttributesMap = {}) {
  const allTgAttributes = [];

  Object.keys(selectedTgAttributesMap).forEach((key) => {
    const tgAttributes = selectedTgAttributesMap[key];
    tgAttributes.forEach((tgAttr) => {
      const { tgName, tgAttrId, tgAttrName } = tgAttr;
      const constructedTgAttributes = {
        id: tgAttrId,
        label: `${tgName} - ${tgAttrName}`,
        ...tgAttr,
      };
      allTgAttributes.push(constructedTgAttributes);
    });
  });

  return allTgAttributes;
}

function constructSelectedZones(selectedRegionAndZonesMap) {
  const allZones = [];

  Object.keys(selectedRegionAndZonesMap).forEach((key) => {
    const zones = selectedRegionAndZonesMap[key];
    zones.forEach((eachZone) => {
      const { zoneId, zoneName } = eachZone || {};
      const constructedZone = {
        id: zoneId,
        label: zoneName,
        ...eachZone,
      };
      allZones.push(constructedZone);
    });
  });

  return allZones;
}

function constructChipsData(data) {
  const constructedData = [];
  data.forEach((item) => {
    const constructedItem = {
      id: item,
      label: item,
    };
    constructedData.push(constructedItem);
  });
  return constructedData;
}

function constructSelectedTouchPoints(selectedTouchPoints = []) {
  const constructedData = [];
  selectedTouchPoints.forEach((touchPoint) => {
    const constructedItem = {
      id: touchPoint,
      label: touchPoint,
    };
    constructedData.push(constructedItem);
  });
  return constructedData;
}

function constructSelectedDataLayers(selectedDataLayers = []) {
  const constructedData = [];
  selectedDataLayers.forEach((dataLayer) => {
    const constructedItem = {
      id: dataLayer.id,
      label: dataLayer.name,
    };
    constructedData.push(constructedItem);
  });
  return constructedData;
}

// checking selected content present or not
function checkIsSelectedContentPresent(...arrays) {
  return arrays.some((array) => array.length > 0);
}

// Selected Tg Chips
function SelectedTargetGroupsChips({ constructedTargetGroups = [] }) {
  const dispatch = useDispatch();

  // functions
  function handleUnSelectTgAttr(_id, tgAttr) {
    dispatch(unSelectTgAttribute(tgAttr));
  }

  if (constructedTargetGroups.length === 0) {
    return null;
  }

  return (
    <Chips
      title="Target Group"
      selectedItems={constructedTargetGroups}
      showHrTag={true}
      containerClassName="flex-wrap"
      onDeselect={handleUnSelectTgAttr}
    />
  );
}

// Selected Zones Chips
function SelectedZonesChips({ constructedZones = [] }) {
  const dispatch = useDispatch();

  // functions
  function handleUnSelectZone(_id, zone) {
    dispatch(unSelectZone(zone));
  }

  if (constructedZones.length === 0) {
    return null;
  }

  return (
    <Chips
      title="Zone"
      selectedItems={constructedZones}
      showHrTag={true}
      containerClassName="flex-wrap"
      onDeselect={handleUnSelectZone}
    />
  );
}

function SelectedArterialRoutesChips({ constructedArterialRoutes = [] }) {
  const dispatch = useDispatch();

  function handleUnSelectRouteType(_id, route) {
    dispatch(toggleRouteTypeSelection(route.id));
  }

  if (constructedArterialRoutes.length === 0) {
    return null;
  }

  return (
    <Chips
      title="Route Type"
      selectedItems={constructedArterialRoutes}
      showHrTag={true}
      containerClassName="flex-wrap"
      onDeselect={handleUnSelectRouteType}
    />
  );
}

function SelectedMediaTypesChips({ constructedMediaTypes = [] }) {
  const dispatch = useDispatch();

  function handleUnSelectMediaType(_id, mediaType) {
    dispatch(toggleMediaType([mediaType.id]));
  }

  if (constructedMediaTypes.length === 0) {
    return null;
  }

  return (
    <Chips
      title="Media Type"
      selectedItems={constructedMediaTypes}
      showHrTag={true}
      containerClassName="flex-wrap"
      onDeselect={handleUnSelectMediaType}
    />
  );
}

function SelectedPoiBrandsChips({ poiBrands }) {
  const dispatch = useDispatch();

  function handleUnselect(brandId, brandObj) {
    const { label = "" } = brandObj;
    dispatch(setPoiStatus(label, Status.UNCHECKED));
    dispatch(unSelectPoiBrands([brandId]));
  }

  if (poiBrands.length === 0) {
    return null;
  }

  return (
    <Chips
      title="Points of Interest"
      selectedItems={poiBrands}
      showHrTag={true}
      containerClassName="flex-wrap"
      onDeselect={handleUnselect}
    />
  );
}

function SelectedTouchPointsChips({ constructedTouchPoints = [] }) {
  const dispatch = useDispatch();

  function handleUnselect(id) {
    dispatch(unSelectTouchPoint(id));
  }

  if (constructedTouchPoints.length === 0) {
    return null;
  }

  return (
    <Chips
      title="Touch Point"
      selectedItems={constructedTouchPoints}
      showHrTag={true}
      containerClassName="flex-wrap"
      onDeselect={handleUnselect}
    />
  );
}

function SelectedDataLayerChips({ constructedDataLayers = [] }) {
  const dispatch = useDispatch();

  function handleUnselect(id) {
    dispatch(unselectDataLayer(id));
  }

  if (constructedDataLayers.length === 0) {
    return null;
  }

  return (
    <Chips
      title="Data Layer"
      selectedItems={constructedDataLayers}
      showHrTag={true}
      containerClassName="flex-wrap"
      onDeselect={handleUnselect}
    />
  );
}

function StretchesColorIndication() {
  const stretchColorClassNames = [
    {
      label: "Selected",
      className: "bg-success",
    },
    {
      label: "Un Selected",
      className: "bg-primary",
    },
    {
      label: "Freq Opt Location",
      className: "bg-warning",
    },
    {
      label: "Touch Point",
      className: "bg-purple",
    },
    {
      label: "Target Group",
      className: "bg-danger",
    },
  ];

  return (
    <div className="border-bottom pb-3 mb-3">
      <h6 className="font-weight-bold">Stretches Color Information :</h6>
      <div className="row">
        {stretchColorClassNames.map((type, i) => {
          const { label, className = "" } = type;
          return (
            <div key={i} className="d-flex align-items-center col-4">
              <span
                className={`border p-2 mr-2 rounded-lg ${className}`}
              ></span>
              <span>{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MediasColorIndication() {
  const mediaColorData = [
    {
      label: "Static",
      colorCode: MediaTypeCategoriesColors.static,
    },
    {
      label: "Digital",
      colorCode: MediaTypeCategoriesColors.digital,
    },
  ];

  return (
    <div className="border-bottom pb-3 mb-3">
      <h6 className="font-weight-bold">Medias Color Information :</h6>
      <div className="row">
        {mediaColorData.map((type, i) => {
          const { label, colorCode = "" } = type;
          return (
            <div key={i} className="d-flex align-items-center col-4">
              <span
                className="border p-2 mr-2 rounded-lg"
                style={{ backgroundColor: colorCode }}
              ></span>
              <span>{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Tab Content Preview Form
 */
export default function TabContentPreviewForm() {
  const dispatch = useDispatch();

  // Region And Zones Map
  const selectedRegionAndZonesMap = useSelector(
    (state) => state.zonesAndSubZones.selectedRegionAndZonesMap
  );

  // Arterial Routes
  const selectedArterialRoutesMap = useSelector(
    (state) => state.orgRouteTypes.selectedArterialRoutes
  );
  const selectedArterialRoutes = Object.keys(selectedArterialRoutesMap);

  // Media Types
  const selectedMediaTypesMap = useSelector(
    (state) => state.campaignPlanning.selectedMediaTypes
  );
  const cityConfig = useSelector((state) => state.orgCityConfig.cityConfig);
  const allMediaTypes = getCityConfigAllMediaTypes(cityConfig);
  const selectedMediaTypes = allMediaTypes.filter(
    (type) => selectedMediaTypesMap[type]
  );

  // Target Group
  const selectedTgAttributesMap = useSelector(
    (state) => state.campaignPlanning.selectedTgAttributesMap
  );

  // POI
  const brandIds = useSelector((state) => state.poiSelection.brandIds) || {};
  const brandIdToLabel = useSelector((state) => state.poi.brandIdToLabel);
  const selectedPoiBrands = Object.keys(brandIds).filter(
    (brandId) => brandIds[brandId].selected
  );
  const poiBrands = selectedPoiBrands.map((id) => ({
    id: id,
    label: brandIdToLabel[id],
  }));

  // Touch Points
  const selectedTouchPoints = useSelector(
    (state) => state.campaignPlanning.selectedTouchPoints
  );

  // Data Layers
  const dataLayerDetailsMap = useSelector(
    (state) => state.orgDataLayers.dataLayerDetailsMap
  );
  const dataLayerSelectedMap = useSelector(
    (state) => state.dataLayersSelection.isLayerSelected
  );
  const selectedDataLayers = Object.values(dataLayerDetailsMap).filter(
    (selectedLayer) => dataLayerSelectedMap[selectedLayer.id] && selectedLayer
  );

  // construct data
  const constructedZones = constructSelectedZones(selectedRegionAndZonesMap);
  const constructedArterialRoutes = constructChipsData(selectedArterialRoutes);
  const constructedMediaTypes = constructChipsData(selectedMediaTypes);
  const constructedTargetGroups = constructSelectedTgs(selectedTgAttributesMap);
  const constructedTouchPoints =
    constructSelectedTouchPoints(selectedTouchPoints);
  const constructedDataLayers = constructSelectedDataLayers(selectedDataLayers);

  // check is selected content present or not
  const isSelectedContentPresent = checkIsSelectedContentPresent(
    constructedZones,
    constructedArterialRoutes,
    constructedMediaTypes,
    // constructedTargetGroups,
    poiBrands,
    constructedTouchPoints,
    constructedDataLayers
  );

  return (
    <div
      className="modal fade"
      id={ProohFormDataTargets.tabContentPreviewForm}
      data-backdrop="static"
    >
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          {/* Form Header */}
          <div className="modal-header bg-alt px-5">
            <h4 className="modal-title">Selected Information</h4>
          </div>

          {/* Form Body */}
          <div className="modal-body px-5">
            {/* Stretches Color Indication */}
            <StretchesColorIndication />

            {/* Media Color Indication */}
            <MediasColorIndication />

            {/* if content not present */}
            {!isSelectedContentPresent && (
              <h5 className="text-center py-3 font-italic text-muted">
                No filters selected
              </h5>
            )}

            {/* if content present */}
            {isSelectedContentPresent && (
              <>
                {/* Target Groups */}
                {/* <SelectedTargetGroupsChips
                  constructedTargetGroups={constructedTargetGroups}
                /> */}

                {/* Zones & SubZones */}
                <SelectedZonesChips constructedZones={constructedZones} />

                {/* Arterial Routes */}
                <SelectedArterialRoutesChips
                  constructedArterialRoutes={constructedArterialRoutes}
                />

                {/* Media Types */}
                <SelectedMediaTypesChips
                  constructedMediaTypes={constructedMediaTypes}
                />

                {/* POI */}
                <SelectedPoiBrandsChips poiBrands={poiBrands} />

                {/* Touch Points */}
                <SelectedTouchPointsChips
                  constructedTouchPoints={constructedTouchPoints}
                />

                {/* Data Layers */}
                <SelectedDataLayerChips
                  constructedDataLayers={constructedDataLayers}
                />
              </>
            )}
          </div>

          {/* Form Footer */}
          <div className="modal-footer px-5">
            <button
              type="button"
              className="btn btn-outline-primary btn-action mt-2 mr-3 rounded-lg"
              data-dismiss="modal"
              onClick={() => dispatch(closeTabContentPreviewForm())}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
